import React, { useState } from "react";
import PropTypes from "prop-types";

import { Input, InputAdornment } from "@mui/material";
import { Clear } from "@mui/icons-material";
import { makeStyles } from "tss-react/mui";
import { useCurrencyContext } from "../Forms/CurrencyProvider";

function ControlledInput(props) {
  const [controlledvalue, setControlledValue] = useState(props.value);
  const [isEditing, setIsEditing] = useState(false);

  const { currency } = useCurrencyContext();

  let prefix = props.prefix ?? "";
  let suffix = props.suffix ?? "";

  let value;
  if (props.controlled) {
    value = controlledvalue;
  } else {
    value = props.value;
  }
  let canClear = props.nullable && value != null && !(value === "" && props.type === "number");

  React.useEffect(() => {
    setControlledValue(props.value);
  }, [props.value]);

  const parseValueView = (value) => {
    if (props.type === "currency" && value != null && String(value).length > 0) {
      const formatter = new Intl.NumberFormat("nl-NL", {
        style: "currency",
        currency: props.currency ?? currency ?? "EUR",
        currencyDisplay: "symbol",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

      value = formatter.format(value);
    }

    return isEditing ? value : prefix + (value ?? "") + suffix;
  };

  const parseValue = (value) => {
    if (props.nullable && props.type !== "text" && value === "") {
      value = null;
    }

    if (value != null && (props.type === "number" || props.type === "currency")) {
      value = value.replace(",", ".");
    }

    return value;
  };

  const parseOutputValue = (value) => {
    if (value != null && (props.type === "number" || props.type === "currency")) {
      value = value.replace(",", ".");
    }

    if (props.type === "currency" && value != null && String(value).length > 0) {
      return Number(value);
    }

    if (props.nullable && props.type !== "text" && value === "") {
      value = null;
    }

    if (value != null && props.type === "number") {
      value = Number(value);
    }

    return value;
  };

  const classes = makeStyles()({
    input: { textAlign: props.type === "currency" || props.type === "number" ? "right" : "left" },
  })();

  return (
    <>
      {!isEditing && props.type === "currency" ? (
        <Input
          classes={{ input: classes.input }}
          disabled={props.disabled}
          endAdornment={
            <InputAdornment position={"end"}>
              {suffix}
              {props.nullable ? (
                <Clear
                  onClick={() => {
                    setControlledValue(null);
                    props.onHandleValue(null);
                  }}
                  size={15}
                  style={{ color: "red", cursor: "pointer", visibility: canClear ? "visible" : "hidden" }}
                />
              ) : null}
            </InputAdornment>
          }
          id={props.id}
          inputProps={props.inputProps}
          onBlur={(e) => {
            props.onHandleValue(parseOutputValue(e.target.value));
            setIsEditing(false);
          }}
          onChange={(e) => {
            if (props.controlled) {
              setControlledValue(parseValue(e.target.value));
              props.onHandleValue(parseOutputValue(e.target.value));
            } else {
              props.onHandleValue(parseOutputValue(e.target.value));
            }
          }}
          onFocus={() => {
            if (props.type !== "date" && props.type !== "datetime") {
              setIsEditing(true);
            }
          }}
          style={{ width: props.nullable ? "calc(100% - 15px)" : "100%", ...props.style }}
          type={props.type === "currency" ? "text" : props.type}
          value={parseValueView(value)}
          {...props.InputProps}
        />
      ) : (
        <Input
          classes={{ input: classes.input }}
          disabled={props.disabled}
          endAdornment={
            <InputAdornment position={"end"}>
              {suffix}
              {props.nullable ? (
                <Clear
                  onClick={() => {
                    setControlledValue(null);
                    props.onHandleValue(null);
                  }}
                  size={15}
                  style={{ color: "red", cursor: "pointer", visibility: canClear ? "visible" : "hidden" }}
                />
              ) : null}
            </InputAdornment>
          }
          id={props.id}
          inputProps={{ ...props.inputProps, name: props.name }}
          onBlur={(e) => {
            props.onHandleValue(parseOutputValue(e.target.value));
            setIsEditing(false);
          }}
          onChange={(e) => {
            if (props.controlled) {
              setControlledValue(parseValue(e.target.value));
              props.onHandleValue(parseOutputValue(e.target.value));
            } else {
              props.onHandleValue(parseOutputValue(e.target.value));
            }
          }}
          onFocus={() => {
            if (props.type !== "date" && props.type !== "datetime") {
              setIsEditing(true);
            }
          }}
          style={{
            width: props.nullable ? "calc(100% - 15px)" : "100%",
            ...props.style,
          }}
          type={props.type === "currency" ? "text" : props.type}
          value={value ?? ""}
          {...props.InputProps}
        />
      )}
    </>
  );
}

ControlledInput.defaultProps = {
  type: "text",
  nullable: false,
  controlled: true,
  disabled: false,
};

ControlledInput.propTypes = {
  type: PropTypes.string,
  name: PropTypes.string,
  prefix: PropTypes.string,
  suffix: PropTypes.string,
  onHandleValue: PropTypes.func,
  nullable: PropTypes.bool,
  controlled: PropTypes.bool,
  inputProps: PropTypes.object,
  disabled: PropTypes.bool,
};

export default ControlledInput;
